import type { ReactNode } from "react";

/**
 * A label on the left and its value on the right.
 * - `list` (default): a 14px row in a divided list; `strong` makes the label bold too (a total).
 * - `compact`: a 13px line in a card; the value is cut short with an ellipsis.
 * - `detail`: the value is a row of its own (a pill, a button), right-aligned.
 */
export function KeyValue({
  label,
  strong,
  variant = "list",
  children,
}: {
  label: ReactNode;
  strong?: boolean;
  variant?: "list" | "compact" | "detail";
  children: ReactNode;
}) {
  if (variant === "compact") {
    return (
      <div className="flex items-center gap-2 text-[13px]">
        <span className="shrink-0 text-fg-subtle">{label}</span>
        <span className="flex-1" />
        <span className="min-w-0 truncate text-right font-semibold text-fg">{children}</span>
      </div>
    );
  }
  if (variant === "detail") {
    return (
      <div className="flex items-center gap-2.5">
        <span className="text-sm font-medium text-[#5c5551]">{label}</span>
        <span className="flex-1" />
        <span className="flex min-w-0 items-center justify-end gap-2 text-right">{children}</span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2.5 py-[9px] text-sm">
      <span className={strong ? "font-semibold text-fg" : "text-fg-muted"}>{label}</span>
      <span className="ml-auto text-right font-semibold text-fg">{children}</span>
    </div>
  );
}
